import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { scrollToId } from '../motion/SmoothScroll';
import Magnetic from '../motion/Magnetic';

export const LOGO_SRC = '/brand/pnp-mark.png';

export function Logo({ onClick }) {
  return (
    <Link to="/" className="pnp-logo" onClick={onClick} aria-label="Pick 'N Pray home">
      <img src={LOGO_SRC} alt="" width="42" height="42" />
      <span className="pnp-logo-word">Pick <b>'N</b> Pray</span>
    </Link>
  );
}

/** Jump to a section on the homepage from anywhere on the marketing site. */
export function useSectionLink() {
  const location = useLocation();
  const navigate = useNavigate();
  return (id) => (e) => {
    if (e) e.preventDefault();
    if (location.pathname === '/') {
      if (!scrollToId(id)) navigate(`/#${id}`);
      window.history.replaceState(null, '', `#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };
}

/**
 * Marketing top bar: transparent over the hero, frosted ink once you
 * scroll; a full-screen menu on phones.
 */
export default function MarketingNav() {
  const location = useLocation();
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const sectionLink = useSectionLink();

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const lenis = window.__pnpLenis;
    if (lenis) lenis.stop();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      if (lenis) lenis.start();
      document.body.style.overflow = prev;
    };
  }, [open]);

  const go = (id) => (e) => { setOpen(false); sectionLink(id)(e); };
  const close = () => setOpen(false);
  const isFaq = location.pathname === '/faq';

  return (
    <>
      <header className={`pnp-nav${solid || open ? ' is-solid' : ''}`}>
        <div className="pnp-wrap pnp-nav-bar">
          <Logo onClick={close} />
          <nav className="pnp-nav-links" aria-label="Main">
            <a href="/#how" className="pnp-nav-link" onClick={go('how')}>How it works</a>
            <a href="/#pools" className="pnp-nav-link" onClick={go('pools')}>Pools</a>
            <a href="/#demo" className="pnp-nav-link" onClick={go('demo')}>Try it</a>
            <Link to="/faq" className={`pnp-nav-link${isFaq ? ' is-active' : ''}`}>FAQ</Link>
          </nav>
          <div className="pnp-nav-cta">
            <Link to="/login" className="pnp-nav-link pnp-nav-signin">Sign in</Link>
            <Magnetic strength={0.2}>
              <Link to="/register" className="pnp-btn pnp-btn-primary pnp-btn-sm">Join free</Link>
            </Magnetic>
            <button
              type="button"
              className={`pnp-burger${open ? ' is-open' : ''}`}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="pnp-menu"
              onClick={() => setOpen((v) => !v)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      <div id="pnp-menu" className={`pnp-menu${open ? ' is-open' : ''}`} aria-hidden={!open}>
        {[
          { label: 'How it works', id: 'how' },
          { label: 'Pools', id: 'pools' },
          { label: 'Try it now', id: 'demo' },
        ].map((l, i) => (
          <a key={l.id} href={`/#${l.id}`} className="pnp-menu-link" style={{ transitionDelay: `${open ? 60 + i * 60 : 0}ms` }} onClick={go(l.id)} tabIndex={open ? 0 : -1}>
            {l.label}
          </a>
        ))}
        <Link to="/faq" className="pnp-menu-link" style={{ transitionDelay: `${open ? 240 : 0}ms` }} onClick={close} tabIndex={open ? 0 : -1}>FAQ</Link>
        <div className="pnp-menu-foot">
          <Link to="/register" className="pnp-btn pnp-btn-primary pnp-btn-lg" onClick={close} tabIndex={open ? 0 : -1}>Join free <span className="pnp-btn-arrow" aria-hidden="true">→</span></Link>
          <Link to="/login" className="pnp-btn pnp-btn-ghost pnp-btn-lg" onClick={close} tabIndex={open ? 0 : -1}>Sign in</Link>
        </div>
      </div>
    </>
  );
}
